import React, { useEffect, useRef, useState } from 'react';
import { IOnboardingDocuments } from '@efex-services/documents/documentsSvc';
import { useTranslation } from 'react-i18next';
import {
  MAX_DOCUMENT_NAME_LENGTH,
  EXTENSION_LENGTH,
} from './useFileInput.constants';
// import useFileValidation from '../useFileValidation';
import { UseFileInputProps, UseFileInputReturn } from './useFileInput.types';

const useFileInput = ({
  onChange,
  onDropChange,
  onDelete,
  inputName,
}: UseFileInputProps): UseFileInputReturn => {
  const [isInternalError, setIsInternalError] = useState({
    isError: false,
    errorMessage: '',
  });

  const { t, i18n } = useTranslation();
  const { language } = i18n;

  const fileInputRef = useRef<HTMLInputElement>(null);

  // const { processFileSecurely } = useFileValidation();

  const truncateString = (str: string): string => {
    if (str.length <= MAX_DOCUMENT_NAME_LENGTH) {
      return str;
    }
    return `${str.slice(
      0,
      MAX_DOCUMENT_NAME_LENGTH - EXTENSION_LENGTH
    )}${str.slice(-EXTENSION_LENGTH)}`;
  };

  const handleChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ): Promise<void> => {
    const { files, name } = event.target;

    setIsInternalError({
      isError: false,
      errorMessage: '',
    });

    if (!files || files.length === 0) {
      return;
    }

    const dataTransfer = new DataTransfer();

    const processedFiles = await Promise.all(
      Array.from(files).map(async (file) => {
        // TODO: Uncomment this when we have the processFileSecurely function working
        // const processedFile = await processFileSecurely(file);
        // if (!processedFile) {
        //   setIsInternalError({
        //     isError: true,
        //     errorMessage: t('dangerousFileInputError'),
        //   });
        //   return null;
        // }

        let finalFile = file;
        if (file.name.length > MAX_DOCUMENT_NAME_LENGTH) {
          const truncatedName = truncateString(file.name);
          finalFile = new File([file], truncatedName, {
            type: file.type,
          });
        }

        return finalFile;
      })
    );

    const validFiles = processedFiles.filter(Boolean);
    validFiles.forEach((file) => dataTransfer.items.add(file as File));

    if (validFiles.length === 0) {
      return;
    }

    const customEvent = {
      target: {
        name,
        files: dataTransfer.files,
      },
    } as React.ChangeEvent<HTMLInputElement>;

    onChange?.(customEvent);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClick = (): void => {
    fileInputRef?.current?.click();
  };

  const handleDeleteFile = (
    e: React.MouseEvent,
    document: IOnboardingDocuments
  ): void => {
    e.preventDefault();
    e.stopPropagation();
    onDelete?.(document.id);
  };

  const handleDragOver: React.DragEventHandler<HTMLInputElement> = (
    ev
  ): void => {
    ev.preventDefault();
    ev.stopPropagation();
    ev.dataTransfer.dropEffect = 'copy';
  };

  const handleDrop: React.DragEventHandler<HTMLInputElement> = async (
    e
  ): Promise<void> => {
    e.preventDefault();
    e.stopPropagation();

    const { files } = e.dataTransfer;

    if (!files || files.length === 0) return;

    const dataTransfer = new DataTransfer();

    const processedFiles = await Promise.all(
      Array.from(files).map(async (file) => {
        // TODO: Uncomment this when we have the processFileSecurely function working
        // const processedFile = await processFileSecurely(file);
        // if (!processedFile) return null;

        let finalFile = file;
        if (file.name.length > MAX_DOCUMENT_NAME_LENGTH) {
          const truncatedName = truncateString(file.name);
          finalFile = new File([file], truncatedName, {
            type: file.type,
          });
        }

        return finalFile;
      })
    );

    const validFiles = processedFiles.filter(Boolean);
    validFiles.forEach((file) => dataTransfer.items.add(file as File));

    if (validFiles.length === 0) return;

    onDropChange?.(dataTransfer.files, inputName);
  };

  useEffect(() => {
    if (!isInternalError.isError) return;

    setIsInternalError({
      isError: true,
      errorMessage: t('dangerousFileInputError'),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  return {
    handleChange,
    handleClick,
    handleDeleteFile,
    handleDragOver,
    handleDrop,
    truncateString,
    fileInputRef,
    isInternalError,
  };
};

export default useFileInput;
